/**
 * Logger utility for @spatialwalk/avatarkit-rtc SDK.
 *
 * Supports three log levels: info, warning, error.
 * External consumers can configure the log level at initialization.
 *
 * @packageDocumentation
 */

/**
 * Log levels supported by the SDK.
 */
export type LogLevel = 'info' | 'warning' | 'error' | 'none';

/**
 * Logger configuration options.
 * @internal
 */
export interface LoggerConfig {
  /** Minimum log level to output. Default: 'warning' */
  level?: LogLevel;
  /** Custom prefix for log messages. Default: '[RTC]' */
  prefix?: string;
}

// Log level priority (higher = more important)
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  info: 0,
  warning: 1,
  error: 2,
  none: 3,
};

// Global logger state
let currentLevel: LogLevel = 'warning';
let prefix = '[RTC]';

/**
 * Configure the SDK logger.
 * @internal
 */
export function configureLogger(config: LoggerConfig): void {
  if (config.level !== undefined) {
    currentLevel = config.level;
  }
  if (config.prefix !== undefined) {
    prefix = config.prefix;
  }
}

/**
 * Get current log level.
 * @internal
 */
export function getLogLevel(): LogLevel {
  return currentLevel;
}

/**
 * Check if a log level should be output.
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[currentLevel];
}

/**
 * Format log message with prefix and component name.
 */
function formatMessage(component: string, message: string): string {
  return `${prefix}[${component}] ${message}`;
}

/**
 * Internal logger instance.
 * @internal
 */
export const logger = {
  /**
   * Log info message (debug-level, verbose).
   */
  info(component: string, message: string, ...args: unknown[]): void {
    if (shouldLog('info')) {
      console.log(formatMessage(component, message), ...args);
    }
  },

  /**
   * Log warning message.
   */
  warn(component: string, message: string, ...args: unknown[]): void {
    if (shouldLog('warning')) {
      console.warn(formatMessage(component, message), ...args);
    }
  },

  /**
   * Log error message.
   */
  error(component: string, message: string, ...args: unknown[]): void {
    if (shouldLog('error')) {
      console.error(formatMessage(component, message), ...args);
    }
  },
};
